import Phaser from "phaser";
import type { SynergyGrade } from "../data/moduleSynergyDefinitions.js";
import { getTextDirection, t } from "../i18n/i18n.js";
import { PALETTE, PALETTE_NUM } from "../themes/palette.js";
import type { BattleTabLayout } from "./battleTabLayout.js";
import { computeScrollbarThumb } from "./marqueeScroll.js";
import { drawSynergyIcon } from "./synergyIcon.js";
import { FONT_FAMILY, TYPO } from "./uiTokens.js";

export interface BattleActiveSynergy {
  readonly id: string;
  readonly name: string;
  /** 전투탭 칩에 표시할 짧은 라벨. 없으면 정식 시너지명을 표시한다. */
  readonly label?: string;
  readonly grade: SynergyGrade;
  readonly level: number;
}

interface SynergyBadgeView {
  readonly bg: Phaser.GameObjects.Graphics;
  readonly icon: Phaser.GameObjects.Graphics;
  readonly text: Phaser.GameObjects.Text;
  readonly hit: Phaser.GameObjects.Rectangle;
  width: number;
  hitVisible: boolean;
  hitInteractive: boolean;
}

const BADGE_MIN_W = 72;
const BADGE_H = 16;
const BADGE_ICON = 10;
const BADGE_GAP = 10;
const BADGE_TEXT_PAD = 31;
const BADGE_TEXT_Y_OFFSET = 1;
const BADGE_DRAG_THRESHOLD = 6;
const SCROLLBAR_H = 3;
const SCROLLBAR_MIN_THUMB_W = 28;
const BADGE_COLOR: Readonly<Record<SynergyGrade, number>> = {
  common: 0x9dd6ff,
  advanced: 0xaee8b7,
  rare: 0xd1b7ff,
  legendary: 0xffcf5a,
};

function setActiveIfAvailable(obj: Phaser.GameObjects.GameObject, active: boolean): void {
  (obj as Phaser.GameObjects.GameObject & { setActive?: (value: boolean) => unknown }).setActive?.(active);
}

function setVisibleActive<T extends Phaser.GameObjects.GameObject & { setVisible: (visible: boolean) => T }>(
  obj: T,
  visible: boolean,
): void {
  obj.setVisible(visible);
  setActiveIfAvailable(obj, visible);
}

export function battleActiveSynergyLabels(synergies: readonly BattleActiveSynergy[]): string[] {
  return synergies.map((synergy) => synergy.label ?? synergy.name ?? t(`synergy.${synergy.id}.name`));
}

export class BattleSynergyBadgeStrip {
  private readonly badges: SynergyBadgeView[] = [];
  private readonly scrollHit: Phaser.GameObjects.Rectangle;
  private readonly scrollbarTrack: Phaser.GameObjects.Rectangle;
  private readonly scrollbarThumb: Phaser.GameObjects.Rectangle;
  private readonly maskGraphics: Phaser.GameObjects.Graphics;
  private readonly mask: Phaser.Display.Masks.GeometryMask;
  private layout: BattleTabLayout;
  private visible = true;
  private activeSynergies: readonly BattleActiveSynergy[] = [];
  private renderKey = "\0";
  private scrollX = 0;
  private contentWidth = 0;
  private scrollHitInteractive = true;
  private pointerStartX: number | null = null;
  private pointerStartScrollX = 0;

  constructor(
    private readonly scene: Phaser.Scene,
    initialLayout: BattleTabLayout,
    private readonly onSynergyInfo?: (synergyId: string) => void,
  ) {
    this.layout = initialLayout;
    this.maskGraphics = scene.add.graphics().setVisible(false);
    setActiveIfAvailable(this.maskGraphics, false);
    this.mask = this.maskGraphics.createGeometryMask();
    this.scrollHit = scene.add
      .rectangle(0, 0, initialLayout.synergyRow.width, initialLayout.synergyRow.height, 0x000000, 0.001)
      .setDepth(5)
      .setInteractive({ useHandCursor: true });
    this.scrollbarTrack = scene.add
      .rectangle(0, 0, initialLayout.synergyRow.width, SCROLLBAR_H, PALETTE_NUM.scrollTrack, 0.62)
      .setDepth(5);
    this.scrollbarThumb = scene.add
      .rectangle(0, 0, SCROLLBAR_MIN_THUMB_W, SCROLLBAR_H, PALETTE_NUM.scrollBar, 0.95)
      .setDepth(6);
    this.bindScrollInput(this.scrollHit);
    this.position();
    this.render();
  }

  resize(layout: BattleTabLayout): void {
    this.layout = layout;
    this.position();
    this.updateVisibility();
  }

  update(activeSynergies: readonly BattleActiveSynergy[], force = false): void {
    this.activeSynergies = activeSynergies;
    this.render(force);
  }

  applyLanguage(): void {
    this.render(true);
  }

  setVisible(visible: boolean): void {
    this.visible = visible;
    setVisibleActive(this.scrollbarTrack, false);
    setVisibleActive(this.scrollbarThumb, false);
    setActiveIfAvailable(this.scrollHit, visible);
    this.updateVisibility();
  }

  private position(): void {
    const L = this.layout;
    const row = L.synergyRow;
    const rtl = getTextDirection() === "rtl";
    this.maskGraphics.clear().fillStyle(0xffffff, 1).fillRect(row.x, row.y, row.width, row.height);
    this.scrollHit.setPosition(row.x + row.width / 2, row.y + row.height / 2);
    this.scrollHit.setSize(row.width, row.height);
    const scrollbarY = row.y + row.height - Math.ceil(SCROLLBAR_H / 2);
    this.scrollbarTrack.setPosition(row.x + row.width / 2, scrollbarY);
    this.scrollbarTrack.setSize(row.width, SCROLLBAR_H);

    this.scrollX = Math.max(0, Math.min(this.scrollX, this.maxScrollX()));
    const thumb = computeScrollbarThumb(
      { textWidth: this.contentWidth, viewWidth: row.width },
      -this.scrollX,
      row.x,
      rtl,
      SCROLLBAR_MIN_THUMB_W,
    );
    this.scrollbarThumb.setPosition(thumb.x + thumb.width / 2, scrollbarY);
    this.scrollbarThumb.setSize(thumb.width, SCROLLBAR_H);
    const cy = row.y + row.height / 2;
    let cursor = 0;
    this.badges.forEach((badge) => {
      const width = badge.width;
      const left = rtl
        ? row.x + row.width - cursor - width + this.scrollX
        : row.x + cursor - this.scrollX;
      const cx = left + width / 2;
      badge.bg.setPosition(cx, cy);
      badge.icon.setPosition(rtl ? cx + width / 2 - 13 : cx - width / 2 + 13, cy);
      badge.text.setPosition(
        rtl ? cx - width / 2 + 7 : cx - width / 2 + 23,
        cy + BADGE_TEXT_Y_OFFSET,
      );
      badge.text.setFixedSize(width - BADGE_TEXT_PAD, BADGE_H);
      const visibleLeft = Math.max(left, row.x);
      const visibleRight = Math.min(left + width, row.x + row.width);
      const visibleWidth = Math.max(0, visibleRight - visibleLeft);
      badge.hitVisible = visibleWidth > 0;
      badge.hit.setSize(visibleWidth, BADGE_H);
      badge.hit.setPosition(visibleLeft + visibleWidth / 2, cy);
      cursor += width + BADGE_GAP;
    });
    this.scrollHit.setVisible(this.visible && this.activeSynergies.length > 0);
  }

  private render(force = false): void {
    const labels = battleActiveSynergyLabels(this.activeSynergies);
    const nextRenderKey = this.activeSynergies
      .map((synergy, index) => `${synergy.id}:${synergy.grade}:${synergy.level}:${labels[index] ?? ""}`)
      .join("|");
    if (!force && nextRenderKey === this.renderKey) {
      this.updateVisibility();
      return;
    }
    this.renderKey = nextRenderKey;
    this.ensureBadgeCount(this.activeSynergies.length);
    this.badges.forEach((badge, index) => {
      const synergy = this.activeSynergies[index];
      const label = labels[index] ?? "";
      badge.text.setText(label);
      badge.bg.clear();
      badge.icon.clear();
      if (!synergy) return;
      badge.width = this.measureBadgeWidth(label, badge.text);
      const color = BADGE_COLOR[synergy.grade];
      badge.bg
        .fillStyle(0x181820, 0.94)
        .fillRoundedRect(-badge.width / 2, -BADGE_H / 2, badge.width, BADGE_H, 5)
        .lineStyle(1, color, 0.86)
        .strokeRoundedRect(-badge.width / 2, -BADGE_H / 2, badge.width, BADGE_H, 5);
      drawSynergyIcon(badge.icon, BADGE_ICON, color, 0x252532, 0.95);
    });
    this.contentWidth = this.badges
      .slice(0, this.activeSynergies.length)
      .reduce((sum, badge, index) => sum + badge.width + (index === 0 ? 0 : BADGE_GAP), 0);
    this.position();
    this.updateVisibility();
  }

  private updateVisibility(): void {
    this.badges.forEach((badge, index) => {
      const visible = this.visible && index < this.activeSynergies.length;
      const hitVisible = visible && badge.hitVisible;
      setVisibleActive(badge.bg, visible);
      setVisibleActive(badge.icon, visible);
      setVisibleActive(badge.text, visible);
      setVisibleActive(badge.hit, hitVisible);
      this.setBadgeHitInteractive(badge, hitVisible);
    });
    const rowVisible = this.visible && this.activeSynergies.length > 0;
    const scrollable = rowVisible && this.contentWidth > this.layout.synergyRow.width;
    setVisibleActive(this.scrollHit, rowVisible);
    setVisibleActive(this.scrollbarTrack, scrollable);
    setVisibleActive(this.scrollbarThumb, scrollable);
    this.setScrollHitInteractive(scrollable);
  }

  private setBadgeHitInteractive(badge: SynergyBadgeView, enabled: boolean): void {
    if (badge.hitInteractive === enabled) return;
    badge.hitInteractive = enabled;
    if (enabled) badge.hit.setInteractive({ useHandCursor: true });
    else badge.hit.disableInteractive();
  }

  private setScrollHitInteractive(enabled: boolean): void {
    if (this.scrollHitInteractive === enabled) return;
    this.scrollHitInteractive = enabled;
    if (enabled) this.scrollHit.setInteractive({ useHandCursor: true });
    else this.scrollHit.disableInteractive();
  }

  private ensureBadgeCount(count: number): void {
    while (this.badges.length < count) {
      const index = this.badges.length;
      const bg = this.scene.add.graphics().setDepth(4).setMask(this.mask);
      const icon = this.scene.add.graphics().setDepth(5).setMask(this.mask);
      const text = this.scene.add
        .text(0, 0, "", { fontFamily: FONT_FAMILY, fontSize: TYPO.micro, color: PALETTE.textPrimary })
        .setOrigin(0, 0.5)
        .setDepth(5)
        .setMask(this.mask);
      const hit = this.scene.add
        .rectangle(0, 0, BADGE_MIN_W, BADGE_H, 0x000000, 0.001)
        .setDepth(6)
        .setInteractive({ useHandCursor: true });
      this.bindScrollInput(hit, index);
      this.badges.push({ bg, icon, text, hit, width: BADGE_MIN_W, hitVisible: true, hitInteractive: true });
    }
  }

  private bindScrollInput(hit: Phaser.GameObjects.Rectangle, synergyIndex?: number): void {
    hit.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
      this.pointerStartX = pointer.x;
      this.pointerStartScrollX = this.scrollX;
    });
    hit.on("pointermove", (pointer: Phaser.Input.Pointer) => {
      if (this.pointerStartX === null || !pointer.isDown) return;
      this.setScroll(this.pointerStartScrollX - (pointer.x - this.pointerStartX));
    });
    hit.on("pointerup", (pointer: Phaser.Input.Pointer) => {
      const moved = this.pointerStartX === null ? 0 : Math.abs(pointer.x - this.pointerStartX);
      this.pointerStartX = null;
      if (synergyIndex === undefined || moved > BADGE_DRAG_THRESHOLD) return;
      const synergy = this.activeSynergies[synergyIndex];
      if (synergy) this.onSynergyInfo?.(synergy.id);
    });
    hit.on("wheel", (_pointer: Phaser.Input.Pointer, _dx: number, dy: number) => {
      this.setScroll(this.scrollX + dy);
    });
  }

  private setScroll(next: number): void {
    this.scrollX = Math.max(0, Math.min(next, this.maxScrollX()));
    this.position();
    this.updateVisibility();
  }

  private maxScrollX(): number {
    return Math.max(0, this.contentWidth - this.layout.synergyRow.width);
  }

  private measureBadgeWidth(label: string, text: Phaser.GameObjects.Text): number {
    const measuredWidth = Number.isFinite(text.width) ? text.width : 0;
    const fallbackWidth = Array.from(label).reduce((sum, ch) => sum + (ch.charCodeAt(0) > 127 ? 9 : 6), 0);
    return Math.max(BADGE_MIN_W, Math.ceil(Math.max(measuredWidth, fallbackWidth) + BADGE_TEXT_PAD));
  }
}
